import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, ADD } from "const"
import {
    SilDevice,
    SilProjectDevice,
    SilProjectDeviceNested,
} from "server/models"
import {
    StateType,
    SilDeviceFilter,
    useInput,
    hasAccess,
    hasProjectAccess,
} from "client/utils"
import {
    PRM_SIL_DEVICE,
    PRM_SIL_SUBSYSTEM,
    StoreType,
    SilDeviceType,
    SilDeviceValidation,

    GetMaxSilDevices,
    GetMaxSilSubsystems,
    LoadSilDeviceById,
    LoadSilProjectDevicesByFilter,
    SaveSilDevice,
    SaveDevice,
    AddSilNestedDevice,

    setSilDeviceReloadList,
    initSilDevice,
    initSilProjectDevice,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
    FormGroup,
    Label,
    RadioButton,
    CheckButton,
    SelectDropdown,
    SelectDropdownItem,
    Input,
    Empty,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

let isMounted: boolean

interface CopyProps {
    device: SilDeviceType
    show: boolean
    onClose: () => void
}

const Copy = ({
    device,
    show,
    onClose,
}: CopyProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const projectIds = useSelector((state: StoreType) => state.project.projectIds)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [targetAccount, setTargetAccount] = useState(true)
    const [projectId, setProjectId] = useState<number>(null)
    const [removeOriginLinks, setRemoveOriginLinks] = useState(false)
    const [newName, setNewName] = useInput("")

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [validation, setValidation] = useState<SilDeviceValidation>(null)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const copyDevice = () => {
        setSaveState(PENDING)

        const accountDevice = device as SilDevice
        const projectDevice = device as SilProjectDevice
        const name = newName.trim().length > 0 ? newName.trim() : null

        LoadSilDeviceById(device.id, accountDevice.accountId || null, projectDevice.projectId || null)
        .then((resDevice) => {
            const originDevice = { ...resDevice }
            originDevice.copy += 1

            const accountOriginDevice = originDevice as SilDevice
            const projectOriginDevice = originDevice as SilProjectDevice

            // max number of devices/subsystems
            const { maxDevices } = GetMaxSilDevices(packageSettings, userData)
            const { maxSubsystems } = GetMaxSilSubsystems(packageSettings, userData)
            const maxNumber = projectId ? null : (originDevice.nested ? maxSubsystems : maxDevices)

            let promise: Promise<number>

            if (targetAccount) {
                // copy to account level
                let newDevice = initSilDevice

                Object.keys(newDevice).forEach((key: keyof SilDevice) => {
                    newDevice = {
                        ...newDevice,
                        [key]: accountOriginDevice[key] !== undefined ? accountOriginDevice[key] : null, // copy only available properties
                    }
                })

                newDevice = {
                    ...newDevice,
                    id: null,
                    accountId: userData.accountId,
                    name: name || `${accountOriginDevice.name} (${accountOriginDevice.copy})`,
                    copy: 0,
                    updated: false,
                }

                promise = SaveSilDevice(null, newDevice, maxNumber, lang).then((res) => {
                    if (isMounted && res.validation) {
                        setValidation(res.validation)
                        setSaveState(IDLE)
                    }
                    return res.newDeviceId
                })
            } else {
                // copy to project level
                let newDevice = initSilProjectDevice

                Object.keys(newDevice).forEach((key: keyof SilProjectDevice) => {
                    newDevice = {
                        ...newDevice,
                        [key]: projectOriginDevice[key] !== undefined ? projectOriginDevice[key] : null, // copy only available properties
                    }
                })

                newDevice = {
                    ...newDevice,
                    id: null,
                    projectId,
                    deviceId: accountOriginDevice.accountId ? accountOriginDevice.id : projectOriginDevice.deviceId,
                    name: name || `${projectOriginDevice.name} (${projectOriginDevice.copy})`,
                    copy: 0,
                    updated: false,
                }

                if (removeOriginLinks) {
                    newDevice = {
                        ...newDevice,
                        deviceId: null,
                        qidProductId: null,
                        qidModelId: null,
                        qidSafetyFunctionId: null,
                        qidOptionId: null,
                        qidDatapointId: null,
                    }
                }

                // check if device already exists on project
                const filters: SilDeviceFilter = {
                    projectId,
                    deviceId: newDevice.deviceId,
                    qidProductId: newDevice.qidProductId,
                    qidModelId: newDevice.qidModelId,
                    qidSafetyFunctionId: newDevice.qidSafetyFunctionId,
                    qidOptionId: newDevice.qidOptionId,
                    qidDatapointId: newDevice.qidDatapointId,
                    qidMethod: newDevice.qidMethod,
                }
                promise = LoadSilProjectDevicesByFilter(filters).then((res1) => {
                    // if project level already has this device - stop execution
                    if (
                        res1.length > 0 && (
                            // at least one link to origin device is not null
                            filters.deviceId !== null
                            || filters.qidProductId !== null
                            || filters.qidModelId !== null
                            || filters.qidSafetyFunctionId !== null
                            || filters.qidOptionId !== null
                            || filters.qidDatapointId !== null
                        )
                    ) {
                        if (isMounted) {
                            setValidation({
                                name: originDevice.nested
                                    ? lang.projectLevelAlreadyHasSubsystem
                                    : lang.projectLevelAlreadyHasDevice,
                            })
                            setSaveState(IDLE)
                        }
                        return null
                    }

                    return SaveSilDevice(null, newDevice, maxNumber, lang).then((res2) => {
                        if (isMounted && res2.validation) {
                            setValidation(res2.validation)
                            setSaveState(IDLE)
                        }
                        return res2.newDeviceId
                    })
                })
            }

            promise.then((newDeviceId) => {
                if (!newDeviceId) return

                delete originDevice.id
                delete originDevice.createdAt
                delete originDevice.updatedAt

                const promiseArray: Promise<any>[] = [
                    SaveDevice(resDevice.id, originDevice),
                ]

                if (originDevice.nested) {
                    originDevice.nestedDevices.forEach((nested) => {
                        let newNested = {
                            ...nested,
                            parentDeviceId: newDeviceId,
                        }

                        delete newNested.id
                        delete newNested.createdAt
                        delete newNested.updatedAt

                        // if we copy to another project - clear project device link
                        if (projectOriginDevice.projectId !== projectId) {
                            newNested = {
                                ...newNested,
                                projectDeviceId: null,
                            } as SilProjectDeviceNested
                        }

                        promiseArray.push(
                            AddSilNestedDevice(projectId, newNested),
                        )
                    })
                }

                Promise.all(promiseArray).then(() => {
                    if (isMounted) setSaveState(IDLE)
                    onClose()

                    dispatch(setSilDeviceReloadList(false))
                    setTimeout(() => {
                        dispatch(setSilDeviceReloadList(true))
                    }, 0)
                })
            })
        })
    }

    const selectedProject = projectId ? projectList[projectId] : null

    const accountDeviceAccess = hasAccess(PRM_SIL_DEVICE, ADD)
    const projectDeviceAccess = hasProjectAccess(PRM_SIL_DEVICE, ADD, projectId)

    const accountSubsystemAccess = hasAccess(PRM_SIL_SUBSYSTEM, ADD)
    const projectSubsystemAccess = hasProjectAccess(PRM_SIL_SUBSYSTEM, ADD, projectId)

    const addAccess = projectId
        ? (device.nested ? projectSubsystemAccess : projectDeviceAccess)
        : (device.nested ? accountSubsystemAccess : accountDeviceAccess)

    return (
        <ConfirmWrapper show={show} onClose={onClose}>
            <Confirm>
                <ConfirmTitle>{device.nested ? lang.copySubsystem : lang.copyDevice}</ConfirmTitle>

                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            <RadioButton
                                padding={false}
                                checked={targetAccount}
                                onChange={() => {
                                    setTargetAccount(true)
                                    setProjectId(null)
                                    setValidation(null)
                                }}
                                title={lang.account}
                            />
                            <RadioButton
                                padding={false}
                                checked={!targetAccount}
                                onChange={() => {
                                    setTargetAccount(false)
                                    setProjectId(project && project.id ? project.id : (projectIds[0] || null))
                                    setValidation(null)
                                }}
                                title={lang.project}
                            />
                        </FormGroup>
                        {
                            !targetAccount && (
                                <FormGroup>
                                    <Label>{lang.project}</Label>
                                    <SelectDropdown title={selectedProject ? selectedProject.name : <Empty />}>
                                        {
                                            projectIds.map((id) => (
                                                <SelectDropdownItem
                                                    key={id}
                                                    onClick={() => {
                                                        setProjectId(id)
                                                        setValidation(null)
                                                    }}
                                                >
                                                    {projectList[id].name}
                                                </SelectDropdownItem>
                                            ))
                                        }
                                    </SelectDropdown>
                                </FormGroup>
                            )
                        }
                        {
                            !targetAccount && (
                                <FormGroup>
                                    <CheckButton
                                        padding={false}
                                        title={lang.removeOriginLinks}
                                        checked={removeOriginLinks}
                                        onChange={() => {
                                            setRemoveOriginLinks(!removeOriginLinks)
                                            setValidation(null)
                                        }}
                                    />
                                </FormGroup>
                            )
                        }

                        <FormGroup>
                            <Label>{lang.newName}</Label>
                            <Input type="text" value={newName} onChange={setNewName} placeholder={lang.optionalField} />
                            <Feedback>
                                {
                                    (targetAccount || (!targetAccount && projectId)) // show only when all data selected
                                    && !addAccess && (device.nested ? lang.noAccessToAddSubsystem : lang.noAccessToAddDevice)
                                }
                            </Feedback>
                            <Feedback>{validation && validation.name}</Feedback>
                        </FormGroup>
                    </Col>
                </Row>

                <ConfirmBottom>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                    <Button
                        variant="success"
                        row
                        disabled={!addAccess || (!targetAccount && !projectId)}
                        onClick={copyDevice}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.confirm}
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default Copy
